import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { ElectricMinMaxSelect } from '../../inputs/ElectricMinMaxSelect'
import { FilterDropdown } from '@/components/search/FilterDropdown'
import { getElectricFilterLabelPrettyName } from '@/lib/utilities/search'
import {
  selectSearchParametersFilterByParameter,
  selectSearchSliceByParameter,
} from '@/store/selectors/search'

type ElectricFilterMinMaxProps = {
  filter: string
  isFilterOpen: boolean
  toggleFilter: () => void
  onChange: (params: {
    type: 'multi' | 'single' | 'vehiclePriceTag'
    filter: string
    value: string[] | number
  }) => void
  title: string
}

export const ElectricFilterMinMax = ({
  isFilterOpen,
  toggleFilter,
  onChange,
  filter,
  title,
}: ElectricFilterMinMaxProps) => {
  const availableFilter = useSelector(state => selectSearchSliceByParameter(state, filter))
  const activeFilter = useSelector(state => selectSearchParametersFilterByParameter(state, filter))

  const [activeFilterMin, setActiveFilterMin] = useState<number | undefined>(undefined)
  const [activeFilterMax, setActiveFilterMax] = useState<number | undefined>(undefined)
  const [availableFilterMin, setAvailableFilterMin] = useState([])
  const [availableFilterMax, setAvailableFilterMax] = useState([])
  const [minVal, setMinVal] = useState<number | undefined>(undefined)
  const [maxVal, setMaxVal] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (activeFilter) {
      const activeMin =
        activeFilter.length > 0 && activeFilter[0].length > 0
          ? activeFilter[0].split('-')[0]
          : undefined

      const activeMax =
        activeFilter.length > 0 && activeFilter[0].length > 0
          ? activeFilter[0].split('-')[1]
          : undefined

      setActiveFilterMin(activeMin ? Number(activeMin) : undefined)
      setMinVal(activeMin)

      setActiveFilterMax(activeMax ? Number(activeMax) : undefined)
      setMaxVal(activeMax)
    }
  }, [activeFilter, availableFilterMax, availableFilterMin])

  useEffect(() => {
    if (availableFilter) {
      setAvailableFilterMin(
        availableFilter?.availableValueRangeFilterList
          .map((i: { range: string; resultsCount: number; filterType: string }) =>
            parseInt(i.range.split('-')[0]),
          )
          .filter(
            (i: number) =>
              i < availableFilter.filterMinMaxRange.max &&
              (activeFilterMin ? i !== activeFilterMin : true),
          )
          .filter(
            (i: number) =>
              ((!activeFilterMax && activeFilterMax !== 0) ||
                (activeFilterMax ? i < activeFilterMax : true)) &&
              i !== activeFilterMin,
          )
          .concat(activeFilterMin || activeFilterMin === 0 ? activeFilterMin : [])
          .sort((a: number, b: number) => a - b),
      )

      setAvailableFilterMax(
        availableFilter?.availableValueRangeFilterList
          .map((i: { range: string; resultsCount: number; filterType: string }) =>
            parseInt(i.range.split('-')[1]),
          )
          .filter((i: number) => i > availableFilter.filterMinMaxRange.min)
          .filter(
            (i: number) =>
              ((!activeFilterMin && activeFilterMin !== 0) ||
                (activeFilterMin ? i > activeFilterMin : true)) &&
              i !== activeFilterMax,
          )
          .concat(activeFilterMax ? activeFilterMax : [])
          .sort((a: number, b: number) => a - b),
      )
    }
  }, [availableFilter])

  const handleMinInput = (value?: number | undefined) => {
    const newValue = value || value === 0 ? value : undefined

    setMinVal(newValue)
    if (activeFilterMin) {
      onChangeInputValue(activeFilterMin, 'min')
    }
    onChangeInputValue(newValue, 'min')
  }

  const handleMaxInput = (value?: number | undefined) => {
    const newValue = value ? value : undefined

    setMaxVal(newValue)
    if (activeFilterMax) {
      onChangeInputValue(activeFilterMax, 'max')
    }
    onChangeInputValue(newValue, 'max')
  }

  const onChangeInputValue = (newValue: number | undefined, minOrMax: 'min' | 'max') => {
    const value =
      minOrMax === 'min'
        ? `${newValue || newValue === 0 ? newValue : ''}-${maxVal || ''}`
        : `${minVal || ''}-${newValue ? newValue : ''}`

    onChange({
      type: 'single',
      filter,
      value: value ? (value !== '-' ? [value] : []) : [],
    })
  }

  return (
    <FilterDropdown
      title={title}
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={!!minVal || minVal === 0 || !!maxVal}
      selectedOptions={
        minVal || minVal === 0 || maxVal
          ? [
              `${getElectricFilterLabelPrettyName(filter, `${minVal}-${maxVal}`, undefined, true, 'first')}`,
            ]
          : ['All']
      }
      maximumNumberOfVisibleSelectedOptions={1}>
      <div>
        <ElectricMinMaxSelect
          title={title}
          filter={filter}
          activeMin={minVal}
          activeMax={maxVal}
          handleMinInput={handleMinInput}
          handleMaxInput={handleMaxInput}
          availableMaximum={availableFilterMax}
          availableMinimum={availableFilterMin}
        />
      </div>
    </FilterDropdown>
  )
}
